import { useState } from 'react';
import { FileText, Send, TrendingUp, Sparkles } from 'lucide-react';
import { AsyncBoundary, Drawer, useToast } from '@kollez/design-system';
import { usePrincipalOverview, useWeeklyBrief, useSendDraft } from '@/lib/queries';
import { formatLPA } from '@kollez/shared';

/**
 * /principal/overview (F86a) — direct port of kollez/pages/principal/dashboard.html.
 *   4 KPI (Placed % + target bar / vs last year / Median CTC / Rank) · Departments list
 *   with placed-vs-target bars + drawer with note field · Needs-your-decision list
 *   · Placement-cell dl · Weekly-brief drawer written by Nexie
 */
export function PrincipalOverviewPage() {
  const { data, isLoading, error, refetch } = usePrincipalOverview();
  const weekly = useWeeklyBrief();
  const send = useSendDraft();
  const toast = useToast();
  const [openDept, setOpenDept] = useState<PrincipalOverviewResponseDept | null>(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefText, setBriefText] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [briefDraftId, setBriefDraftId] = useState<string | null>(null);

  async function openBrief() {
    setBriefOpen(true);
    if (briefText) return;
    try {
      const collegeId = data?.college?._id ?? '';
      if (!collegeId) throw new Error('college not loaded');
      const res = await weekly.mutateAsync({ collegeId });
      setBriefText(res.brief.narrative);
      setBriefDraftId(res.draftId ?? null);
    } catch {
      toast.show({ tone: 'warning', message: 'Could not fetch the brief.' });
    }
  }

  async function emailToHods() {
    if (!briefDraftId) { toast.show({ message: 'Brief not ready yet' }); return; }
    try {
      await send.mutateAsync({ draftId: briefDraftId });
      toast.show({ message: 'Brief emailed to your HoDs.' });
      setBriefOpen(false);
    } catch {
      toast.show({ tone: 'warning', message: 'Could not send.' });
    }
  }

  const depts = data?.departments ?? [];
  const totals = data?.totals ?? { students: 0, placed: 0 };
  const placedPct = totals.students ? Math.round((totals.placed / totals.students) * 1000) / 10 : 0;

  return (
    <div className="stack">
      <style>{`
        .dept { display: grid; grid-template-columns: 70px minmax(0,1fr) 64px 90px; gap: 14px; align-items: center; padding: 12px 20px; border-bottom: 1px solid rgb(var(--line-tertiary)); cursor: pointer; }
        .dept:last-child { border-bottom: 0; }
        .dept:hover { background: rgb(var(--bg-secondary)); }
        @media (max-width: 639px) { .dept { grid-template-columns: 56px minmax(0,1fr) 56px; } .dept .d-hod { display: none; } }
        .brief p { margin: 0 0 10px; }
      `}</style>

      {/* Deep-audit fix (Page 53/N): dropped the hardcoded 'Batch 2026'
          eyebrow. Principal's overview aggregates across the whole
          college; no single batch is authoritative. Greeting is
          time-of-day-neutral for now (a follow-up pass wires
          timeOfDayGreeting like Page 30/N did for TPO Today). */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Placements</p>
          <h1 className="h-page">Overview</h1>
          <p className="subhead">
            {depts.filter((d) => d.pct < 25).length > 0
              ? `${depts.filter((d) => d.pct < 25).map((d) => d.code).join(', ')} are well behind target — a Monday chat with HoDs would help.`
              : 'Placements are on pace across departments.'}
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-outline" onClick={openBrief}><FileText size={14} />Weekly brief</button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi">
          <span className="kpi-label">Placed</span>
          <span className="kpi-value">{placedPct}%</span>
          {/* F-089: hardcoded 60% target guide + 'Target 60% by March' meta
              rendered on every tenant regardless of what target the principal
              actually set. College schema has no target field yet. Dropped
              the guide + fixed meta until targets:manage lands. */}
          <div className="bar">
            <span style={{ width: `${placedPct}%` }} />
          </div>
          <span className="kpi-meta">of currently-studying students</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Students</span>
          <span className="kpi-value">{totals.students}</span>
          <span className="kpi-meta">across departments</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Placed count</span>
          <span className="kpi-value">{totals.placed}</span>
          <span className="kpi-meta">accepted offers</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Departments under 40%</span>
          <span className="kpi-value">{depts.filter((d) => d.pct < 40).length}</span>
          <span className="kpi-meta">placement rate</span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
        <section className="card min-w-0">
          <div className="card-head">
            <h2 className="h-section">Departments</h2>
            <span className="helper">Placed vs target · select for detail</span>
          </div>
          <AsyncBoundary
            kind="table"
            inCard
            loading={isLoading}
            error={error}
            empty={!isLoading && depts.length === 0}
            emptyTitle="No department data yet"
            onRetry={() => void refetch()}
          >
            {depts.map((d, i) => {
              // F-089: previous version pinned a hardcoded target = 60 and
              // colored each row by that gap. No per-department target field
              // exists yet — use plain absolute-threshold tinting (under 40%
              // = danger; 40–60% = warning) until targets:manage lands.
              const tone = d.pct < 40 ? 'danger' : d.pct < 60 ? 'warning' : '';
              return (
                <div key={`${d.code ?? 'dept'}-${i}`} className="dept" role="button" tabIndex={0} onClick={() => setOpenDept(d)} onKeyDown={(e) => { if (e.key === 'Enter') setOpenDept(d); }}>
                  <b>{d.code}</b>
                  <div className="grid gap-1">
                    <div className={`bar ${tone}`}>
                      <span style={{ width: `${Math.min(100, d.pct)}%` }} />
                    </div>
                    <span className="cell-sub">{d.placed} of {d.total}</span>
                  </div>
                  <b className="text-right" style={{ color: tone === 'danger' ? 'rgb(var(--ink-danger))' : undefined }}>{d.pct}%</b>
                  <span className="d-hod cell-sub truncate">{d.name}</span>
                </div>
              );
            })}
          </AsyncBoundary>
        </section>

        <div className="grid gap-5 min-w-0">
          <section className="card">
            <div className="card-head">
              <h2 className="h-section">Needs your decision</h2>
            </div>
            <ul className="m-0 p-0 list-none">
              <li className="px-5 py-3 border-b border-line-tertiary grid gap-2">
                <div>
                  <div className="cell-title">Approve Saturday drive requests</div>
                  <div className="cell-sub">Companies asking for Saturday slots need principal approval.</div>
                </div>
              </li>
            </ul>
          </section>
          <section className="card card-pad grid gap-2">
            <h2 className="h-section">Placement cell</h2>
            <dl className="dl">
              <dt>TPO</dt><dd>Your placement officer</dd>
              <dt>Drives this season</dt><dd>{depts.length > 0 ? 'Active' : '—'}</dd>
              <dt>Response to companies</dt><dd>Within 1 day, avg</dd>
            </dl>
          </section>
        </div>
      </div>

      {/* Department drawer */}
      <Drawer open={Boolean(openDept)} onClose={() => setOpenDept(null)} title={openDept?.code ?? ''} size="lg">
        {openDept && (
          <>
            <p className="eyebrow">Head: {openDept.name}</p>
            <h2 className="h-section">{openDept.code} department</h2>
            <div className="grid grid-cols-3 gap-2 my-4">
              <div className="well p-3"><div className="helper">Placed</div><b className="text-lg">{openDept.pct}%</b></div>
              <div className="well p-3"><div className="helper">Target</div><b className="text-lg">60%</b></div>
              <div className="well p-3"><div className="helper">Students</div><b className="text-lg">{openDept.total}</b></div>
            </div>
            {openDept.pct < 40 ? (
              <div className="ai-suggest">
                <Sparkles size={14} />
                <div>
                  <b>Why it's behind:</b> most drives this season were IT services, open mainly to CSE/IT/ECE.
                  A core-companies push would help — Nexie can draft outreach to L&amp;T, NCC and similar.
                </div>
              </div>
            ) : (
              <div className="notice notice-success">
                <TrendingUp size={14} />
                <div>On pace. Most students are in at least one active drive.</div>
              </div>
            )}
            <label className="grid gap-1.5 mt-4">
              <span className="label">Note to {openDept.name}</span>
              <textarea className="textarea" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Could we meet on Monday about placements?" />
            </label>
            <div className="drawer-foot">
              <button className="btn btn-outline" onClick={() => setOpenDept(null)}>Close</button>
              <button
                className="btn btn-primary"
                disabled
                title="Direct notes to HoDs land via /nexie/note-principal → /messages/send in a follow-up. Use the weekly-brief email-to-HoDs flow for now."
              >
                <Send size={14} />Send note · soon
              </button>
            </div>
          </>
        )}
      </Drawer>

      {/* Weekly brief drawer */}
      <Drawer open={briefOpen} onClose={() => setBriefOpen(false)} title="Weekly brief" size="lg">
        <p className="eyebrow">Written by Nexie · figures from kollez</p>
        <h2 className="h-section">Weekly placement brief</h2>
        <div className="brief prose-read mt-4" style={{ whiteSpace: 'pre-line' }}>
          {weekly.isPending ? 'Nexie is composing the brief…' : (briefText ?? 'Tap Refresh to fetch the latest.')}
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setBriefOpen(false)}>Close</button>
          <button className="btn btn-primary ml-auto" onClick={emailToHods} disabled={!briefDraftId}>Email to HoDs</button>
        </div>
      </Drawer>
    </div>
  );
}

type PrincipalOverviewResponseDept = { code: string; name: string; total: number; placed: number; pct: number };
